const LONG_LENGTH_VRS = new Set(['OB', 'OD', 'OF', 'OL', 'OW', 'SQ', 'UC', 'UN', 'UR', 'UT']);
const STRING_VRS = new Set(['AE', 'AS', 'CS', 'DA', 'DS', 'DT', 'IS', 'LO', 'LT', 'PN', 'SH', 'ST', 'TM', 'UI', 'UT', 'UC', 'UR']);
const UNDEFINED_LENGTH = 0xffffffff;
const ITEM_TAG = 'fffee000';
const SEQUENCE_DELIMITER_TAG = 'fffee0dd';

const textEncoder = new TextEncoder();

class ByteWriter {
    constructor(initialCapacity = 1024) {
        this.buffer = new Uint8Array(initialCapacity);
        this.view = new DataView(this.buffer.buffer);
        this.length = 0;
    }

    ensure(extra) {
        const needed = this.length + extra;
        if (needed <= this.buffer.length) return;
        let capacity = this.buffer.length * 2;
        while (capacity < needed) capacity *= 2;
        const grown = new Uint8Array(capacity);
        grown.set(this.buffer.subarray(0, this.length));
        this.buffer = grown;
        this.view = new DataView(grown.buffer);
    }

    uint8(value) {
        this.ensure(1);
        this.buffer[this.length] = value;
        this.length += 1;
    }

    uint16(value) {
        this.ensure(2);
        this.view.setUint16(this.length, value, true);
        this.length += 2;
    }

    uint32(value) {
        this.ensure(4);
        this.view.setUint32(this.length, value, true);
        this.length += 4;
    }

    bytes(data) {
        this.ensure(data.length);
        this.buffer.set(data, this.length);
        this.length += data.length;
    }

    result() {
        return this.buffer.slice(0, this.length);
    }
}

const encodeStringValue = (vr, value) => {
    const joined = Array.isArray(value) ? value.join('\\') : String(value);
    const encoded = textEncoder.encode(joined);
    if (encoded.length % 2 === 0) return encoded;
    const padded = new Uint8Array(encoded.length + 1);
    padded.set(encoded);
    padded[encoded.length] = vr === 'UI' ? 0x00 : 0x20;
    return padded;
};

const encodeValue = (vr, value) => {
    if (STRING_VRS.has(vr)) return encodeStringValue(vr, value);
    if (vr === 'US') {
        const values = Array.isArray(value) ? value : [value];
        const out = new Uint8Array(values.length * 2);
        const view = new DataView(out.buffer);
        values.forEach((v, i) => view.setUint16(i * 2, v, true));
        return out;
    }
    if (vr === 'UL') {
        const values = Array.isArray(value) ? value : [value];
        const out = new Uint8Array(values.length * 4);
        const view = new DataView(out.buffer);
        values.forEach((v, i) => view.setUint32(i * 4, v, true));
        return out;
    }
    if (vr === 'OB' || vr === 'OW') {
        const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
        if (bytes.length % 2 === 0) return bytes;
        const padded = new Uint8Array(bytes.length + 1);
        padded.set(bytes);
        return padded;
    }
    throw new Error(`dicomWriter: unsupported VR ${vr}`);
};

const writeTag = (writer, tag) => {
    writer.uint16(parseInt(tag.slice(0, 4), 16));
    writer.uint16(parseInt(tag.slice(4, 8), 16));
};

const writeElementHeader = (writer, tag, vr, length) => {
    writeTag(writer, tag);
    writer.uint8(vr.charCodeAt(0));
    writer.uint8(vr.charCodeAt(1));
    if (LONG_LENGTH_VRS.has(vr)) {
        writer.uint16(0);
        writer.uint32(length);
    } else {
        writer.uint16(length);
    }
};

const writeElement = (writer, { tag, vr, value }) => {
    const encoded = encodeValue(vr, value);
    writeElementHeader(writer, tag, vr, encoded.length);
    writer.bytes(encoded);
};

const writeEncapsulatedPixelData = (writer, fragments) => {
    writeElementHeader(writer, '7fe00010', 'OB', UNDEFINED_LENGTH);
    writeTag(writer, ITEM_TAG);
    writer.uint32(0);
    fragments.forEach((fragment) => {
        const padded = encodeValue('OB', fragment);
        writeTag(writer, ITEM_TAG);
        writer.uint32(padded.length);
        writer.bytes(padded);
    });
    writeTag(writer, SEQUENCE_DELIMITER_TAG);
    writer.uint32(0);
};

const sortByTag = (elements) => [...elements].sort((a, b) => (a.tag < b.tag ? -1 : a.tag > b.tag ? 1 : 0));

const buildFileMetaInformation = ({ sopClassUID, sopInstanceUID, transferSyntaxUID, implementationClassUID, implementationVersionName }) => {
    const body = new ByteWriter(256);
    writeElement(body, { tag: '00020001', vr: 'OB', value: new Uint8Array([0x00, 0x01]) });
    writeElement(body, { tag: '00020002', vr: 'UI', value: sopClassUID });
    writeElement(body, { tag: '00020003', vr: 'UI', value: sopInstanceUID });
    writeElement(body, { tag: '00020010', vr: 'UI', value: transferSyntaxUID });
    writeElement(body, { tag: '00020012', vr: 'UI', value: implementationClassUID });
    writeElement(body, { tag: '00020013', vr: 'SH', value: implementationVersionName });
    const bodyBytes = body.result();

    const meta = new ByteWriter(bodyBytes.length + 16);
    writeElement(meta, { tag: '00020000', vr: 'UL', value: bodyBytes.length });
    meta.bytes(bodyBytes);
    return meta.result();
};

export const dicomWriter = {
    writePart10File: ({ fileMeta, elements, encapsulatedPixelDataFragments }) => {
        const writer = new ByteWriter(64 * 1024);
        writer.bytes(new Uint8Array(128));
        writer.bytes(textEncoder.encode('DICM'));
        writer.bytes(buildFileMetaInformation(fileMeta));
        sortByTag(elements).forEach((element) => writeElement(writer, element));
        if (encapsulatedPixelDataFragments) {
            writeEncapsulatedPixelData(writer, encapsulatedPixelDataFragments);
        }
        return writer.result();
    },
};
